// The brand a printed piece carries.
//
// REVISED 2026-08-09. This replaces a hard rule that used to sit at the top of
// `tokens.ts`:
//
//   "the material we produce carries Help Genie brand only. The customer's name
//    goes in the copy. Their colours and their logo never appear on a printed
//    piece."
//
// That rule turned a PrintIQ pack into a Help Genie pack with "PrintIQ" typed
// into the sentences — Help Genie's mark at the top of every sheet, Help Genie's
// navy behind it, on a poster PrintIQ is sticking to their own front door. The
// artefact belongs to the customer. Help Genie signs it at the foot, small.
//
// Everything here is derived rather than trusted. A brand colour picked in a
// web colour picker is chosen against a screen, and a poster is a different
// medium: pale brand colours that look fine behind white type on a monitor
// print as an unreadable sheet. So each role is pushed to a contrast floor
// before it is used, keeping the customer's hue and giving up only lightness.
//
// A genie with no branding at all resolves to the Help Genie palette, which is
// exactly what these documents printed before — so an unbranded genie's pack
// does not change.

import type { GenieBranding } from '@/types/ui/branding';

export interface PrintBrand {
  /** Headline colour and the ground of the primary-toned sheets. Carries white type. */
  primary: string;
  /** Accent rules, secondary-toned sheets, "do not" markers. Carries white type. */
  secondary: string;
  /** The call-to-action colour on the dark sheets. Readable against `inkDeep`. */
  highlight: string;
  /** Headings and body emphasis on white sheets. */
  ink: string;
  /** The dark ground of the cover and the hero posters. Also the QR module colour. */
  inkDeep: string;
  /** A pale wash of the primary, for panels that sit behind dark text. */
  tint: string;
  /** Alternating table rows — one step up from `tint`. */
  rowAlt: string;
  /** The customer's logo, or null when they have not uploaded one. */
  logoUrl: string | null;
  /** Printed beside the logo, and instead of it when there is none. */
  name: string;
  /** True when nothing was branded and this is the Help Genie palette. */
  isDefault: boolean;
}

/**
 * The Help Genie palette, which is what an unbranded genie falls back to. These
 * are the exact values every pack printed before this file existed.
 */
const HG = {
  primary: '#4E9CFF',
  secondary: '#FF4DB8',
  highlight: '#FFBB33',
  ink: '#28406F',
  inkDeep: '#1A2C4F',
  tint: '#F9FAFC',
  rowAlt: '#F4F6FB',
} as const;

/**
 * Contrast floors, in ratio against the colour the type sits on.
 *
 * 2.6 rather than the 4.5 of WCAG body text: these are display sizes on paper,
 * and Help Genie's own Spark Blue sits at 2.8 against white. Holding a customer
 * to a stricter floor than the house palette meets would darken almost every
 * brand on file into something the customer would not recognise.
 */
const ON_COLOUR_FLOOR = 2.6;
/** The CTA line has to lift off the dark ground it prints on. */
const HIGHLIGHT_FLOOR = 4.5;
/**
 * QR modules. The finder patterns are what a phone camera locks onto first, and
 * a low-contrast code fails in the exact conditions a poster lives in — a lit
 * shop window, a corridor, a dim workshop. Help Genie navy sits at about 13:1
 * and that is the bar the derived ink has to clear too.
 */
const QR_INK_FLOOR = 10;

type Rgb = [number, number, number];

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

const parseHex = (value?: string | null): Rgb | null => {
  const raw = (value ?? '').trim().replace(/^#/, '');
  const hex = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
};

const toHex = (rgb: Rgb): string =>
  `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

/** WCAG relative luminance. */
const luminance = (rgb: Rgb): number => {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a: Rgb, b: Rgb): number => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const WHITE: Rgb = [255, 255, 255];

const toHsl = ([r, g, b]: Rgb): [number, number, number] => {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === rn ? ((gn - bn) / d + (gn < bn ? 6 : 0)) : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4;
  return [h / 6, s, l];
};

const fromHsl = ([h, s, l]: [number, number, number]): Rgb => {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [hue(h + 1 / 3) * 255, hue(h) * 255, hue(h - 1 / 3) * 255];
};

const withLightness = (rgb: Rgb, l: number): Rgb => {
  const [h, s] = toHsl(rgb);
  return fromHsl([h, s, clamp01(l)]);
};

/**
 * Walks lightness in one direction until the colour clears `floor` against
 * `against`, keeping hue and saturation. Returns the original when it already
 * clears, so a brand that is fine is never touched.
 */
const pushUntil = (rgb: Rgb, against: Rgb, floor: number, direction: -1 | 1): Rgb => {
  if (contrast(rgb, against) >= floor) return rgb;
  const [, , start] = toHsl(rgb);
  for (let step = 1; step <= 50; step += 1) {
    const l = clamp01(start + direction * step * 0.02);
    const next = withLightness(rgb, l);
    if (contrast(next, against) >= floor) return next;
    if (l === 0 || l === 1) break;
  }
  return withLightness(rgb, direction === -1 ? 0.08 : 0.94);
};

/** Darkens until white type reads on it. */
const carriesWhite = (rgb: Rgb, floor = ON_COLOUR_FLOOR): Rgb => pushUntil(rgb, WHITE, floor, -1);

/**
 * Two roles that must not collide. A customer whose primary and secondary are
 * near-identical would otherwise print an accent rule that vanishes into the
 * sheet it is drawn on.
 */
const separate = (rgb: Rgb, from: Rgb): Rgb => {
  if (contrast(rgb, from) >= 1.35) return rgb;
  const [h, s, l] = toHsl(rgb);
  return fromHsl([(h + 0.5) % 1, Math.max(s, 0.5), l]);
};

/**
 * The printed brand for one pack.
 *
 * `name` is passed rather than read off the branding because the pack already
 * resolves a display name for its target, and the two must not disagree — the
 * name under the logo on the cover is the name in the copy on page four.
 */
export const resolvePrintBrand = (
  branding: Partial<GenieBranding> | null | undefined,
  name: string,
): PrintBrand => {
  const rawPrimary = parseHex(branding?.primaryColor);
  const logoUrl = (branding?.logoUrl || branding?.customAvatarUrl || '').trim() || null;

  // Nothing branded at all: the house palette, unchanged. A genie that has
  // never had a colour picked still prints the document it printed yesterday.
  if (!rawPrimary) {
    return {
      ...HG,
      logoUrl,
      name,
      isDefault: !logoUrl,
    };
  }

  const primary = carriesWhite(rawPrimary);
  const rawSecondary = parseHex(branding?.secondaryColor);
  const secondary = carriesWhite(separate(rawSecondary ?? withLightness(rawPrimary, 0.55), primary));

  // The dark ground, in the customer's own hue. Pushed past the QR floor rather
  // than the type floor: this colour is also what every code in the document
  // prints in, and a code is read by a camera, not an eye.
  const inkDeep = carriesWhite(withLightness(primary, 0.14), QR_INK_FLOOR);
  const ink = carriesWhite(withLightness(primary, 0.26), 7);

  // The CTA on the dark sheets. Lifted off `inkDeep` rather than off white,
  // because that is the only ground it ever prints on.
  const highlight = pushUntil(rawSecondary ?? rawPrimary, inkDeep, HIGHLIGHT_FLOOR, 1);

  return {
    primary: toHex(primary),
    secondary: toHex(secondary),
    highlight: toHex(highlight),
    ink: toHex(ink),
    inkDeep: toHex(inkDeep),
    tint: toHex(withLightness(primary, 0.975)),
    rowAlt: toHex(withLightness(primary, 0.955)),
    logoUrl,
    name,
    isDefault: false,
  };
};

/** Exposed for the brand's own tests and for the print spec page. */
export const printBrandInternals = { parseHex, toHex, contrast, luminance, withLightness };
